import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';

export type Modality = 'TEXT' | 'STRUCTURED' | 'AUDIO';

type ModelModality = {
  modality: Modality;
  scaleFactor: number;
};

export type ReasoningEffort = 'none' | 'low' | 'medium' | 'high' | 'xhigh' | 'max';

export type LlmModel = {
  id: string;
  providerKey: string;
  name: string;
  maxInputTokens: number;
  tpmLimit: number | null;
  rpmLimit: number | null;
  enabled: boolean;
  reasoningEffort: ReasoningEffort | null;
  temperature: number | null;
  modalities: ModelModality[];
  createdAt: string | null;
};

export type LlmProvider = {
  id: string;
  key: string;
  enabled: boolean;
  tpmLimit: number | null;
  rpmLimit: number | null;
};

type LlmTaskPoolEntry = {
  model: LlmModel;
  enabled: boolean;
};

export type LlmTaskWithPool = {
  id: string;
  key: string;
  displayName: string;
  modality: Modality;
  pool: LlmTaskPoolEntry[];
};

type LlmTaskPoolMember = {
  taskId: string;
  modelId: string;
  enabled: boolean;
};

type PoolMembership = {
  taskId: string;
  modelId: string;
};

type UpdateModelArgs = {
  modelId: string;
  name: string;
  tpmLimit: number | null;
  rpmLimit: number | null;
  maxInputTokens: number;
  reasoningEffort: ReasoningEffort | null;
  temperature: number | null;
};

type AddModelArgs = {
  providerKey: string;
  name: string;
  maxInputTokens: number;
  tpmLimit: number | null;
  rpmLimit: number | null;
  reasoningEffort: ReasoningEffort | null;
  temperature: number | null;
  modalities: ModelModality[];
};

export const useLlmModels = () => {
  const { data, isLoading, isError, error, refetch } = useQuery<LlmModel[]>({
    queryKey: ['admin', 'llm-models'],
  });

  return { models: data, isLoading, isError, error, refetch };
};

export const useLlmTasks = () => {
  const { data, isLoading, isError, error, refetch } = useQuery<LlmTaskWithPool[]>({
    queryKey: ['admin', 'llm-tasks'],
  });

  return { tasks: data, isLoading, isError, error, refetch };
};

export const useLlmProviders = () => {
  const { data, isLoading, isError, error, refetch } = useQuery<LlmProvider[]>({
    queryKey: ['admin', 'llm-providers'],
  });

  return { providers: data, isLoading, isError, error, refetch };
};

export const useAddModelToPool = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation<unknown, Error, PoolMembership>({
    mutationFn: ({ taskId, modelId }) => api.post(`admin/llm-tasks/${taskId}/pool`, { modelId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'llm-tasks'] });
    },
  });

  return { addModelToPool: mutateAsync, isLoading };
};

export const useRemoveModelFromPool = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation<unknown, Error, PoolMembership>({
    mutationFn: ({ taskId, modelId }) => api.delete(`admin/llm-tasks/${taskId}/pool/${modelId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'llm-tasks'] });
    },
  });

  return { removeModelFromPool: mutateAsync, isLoading };
};

const invalidateCatalogue = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ['admin', 'llm-models'] });
  queryClient.invalidateQueries({ queryKey: ['admin', 'llm-providers'] });
  queryClient.invalidateQueries({ queryKey: ['admin', 'llm-tasks'] });
};

export const useAddModel = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation<
    { data: LlmModel },
    Error,
    AddModelArgs
  >({
    mutationFn: payload => api.post<LlmModel>('admin/llm-models', payload),
    onSuccess: () => invalidateCatalogue(queryClient),
  });

  return { addModel: mutateAsync, isLoading };
};

export const useUpdateModel = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation<
    { data: LlmModel },
    Error,
    UpdateModelArgs
  >({
    mutationFn: ({ modelId, ...payload }) =>
      api.patch<LlmModel>(`admin/llm-models/${modelId}`, payload),
    onSuccess: () => invalidateCatalogue(queryClient),
  });

  return { updateModel: mutateAsync, isLoading };
};

export const useDeleteModel = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation<unknown, Error, { modelId: string }>({
    mutationFn: ({ modelId }) => api.delete(`admin/llm-models/${modelId}`),
    onSuccess: () => invalidateCatalogue(queryClient),
  });

  return { deleteModel: mutateAsync, isLoading };
};

export const useSetModelEnabled = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation<
    { data: LlmModel },
    Error,
    { modelId: string; enabled: boolean }
  >({
    mutationFn: ({ modelId, enabled }) =>
      api.post<LlmModel>(`admin/llm-models/${modelId}/${enabled ? 'enable' : 'disable'}`),
    onSuccess: () => invalidateCatalogue(queryClient),
  });

  return { setModelEnabled: mutateAsync, isLoading };
};

export const useSetProviderEnabled = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation<
    { data: LlmProvider },
    Error,
    { providerId: string; enabled: boolean }
  >({
    mutationFn: ({ providerId, enabled }) =>
      api.post<LlmProvider>(`admin/llm-providers/${providerId}/${enabled ? 'enable' : 'disable'}`),
    onSuccess: () => invalidateCatalogue(queryClient),
  });

  return { setProviderEnabled: mutateAsync, isLoading };
};

export const useSetPoolMemberEnabled = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation<
    { data: LlmTaskPoolMember },
    Error,
    { taskId: string; modelId: string; enabled: boolean }
  >({
    mutationFn: ({ taskId, modelId, enabled }) =>
      api.post<LlmTaskPoolMember>(
        `admin/llm-tasks/${taskId}/pool/${modelId}/${enabled ? 'enable' : 'disable'}`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'llm-tasks'] });
    },
  });

  return { setPoolMemberEnabled: mutateAsync, isLoading };
};

export const useSetModelModality = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation<
    { data: ModelModality },
    Error,
    { modelId: string; modality: Modality; scaleFactor: number }
  >({
    mutationFn: ({ modelId, modality, scaleFactor }) =>
      api.put<ModelModality>(`admin/llm-models/${modelId}/modalities/${modality}`, {
        scaleFactor,
      }),
    onSuccess: () => invalidateCatalogue(queryClient),
  });

  return { setModelModality: mutateAsync, isLoading };
};

export const useRemoveModelModality = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation<
    unknown,
    Error,
    { modelId: string; modality: Modality }
  >({
    mutationFn: ({ modelId, modality }) =>
      api.delete(`admin/llm-models/${modelId}/modalities/${modality}`),
    onSuccess: () => invalidateCatalogue(queryClient),
  });

  return { removeModelModality: mutateAsync, isLoading };
};

export const useSetTaskModality = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation<
    { data: LlmTaskWithPool },
    Error,
    { taskId: string; modality: Modality }
  >({
    mutationFn: ({ taskId, modality }) =>
      api.patch<LlmTaskWithPool>(`admin/llm-tasks/${taskId}`, { modality }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'llm-tasks'] });
    },
  });

  return { setTaskModality: mutateAsync, isLoading };
};
